import { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Eye, FileSpreadsheet } from "lucide-react";
import { BackButton } from "../../../components/button/back-button";
import { LoadingButton } from "../../../components/button/loading-button";
import { MetricHelp } from "../../../components/metric-help";
import { Table } from "../../../components/table";
import { ViewConversationModal } from "../../../components/view-conversation-modal";
import { useToast } from "../../../contexts/toast/toast-context";
import { useAuth } from "../../../hooks/use-auth";
import { useDebounce } from "../../../hooks/use-debounce";
import api from "../../../services/api";
import type { Conversation } from "../../../types/conversation";
import type { Tenant } from "../../../types/tenant";
import { formatPhoneWithCountryCode, getApiErrorMessage } from "../../../utils";
import {
  getBrowserTimezone,
  resolvePresetRange,
  toReportPeriodQuery,
  type ReportGranularity,
  type ReportPreset,
} from "../../../utils/report-period";

type ClosedConversationStatus =
  | "CLOSED_BY_AI"
  | "CLOSED_BY_HUMAN_ATTENDANCE"
  | "CLOSED_BY_TIMEOUT";

type ConversationDto = {
  id: string;
  appName: string;
  userName: string;
  countryCode: string;
  dialCode: string;
  status: ClosedConversationStatus;
  systemUserId?: string;
  systemUserName?: string;
  channel: string;
  createdAt: string;
};

type WindowUsedDto = {
  start: string;
  end: string;
  tz: string;
  granularity: ReportGranularity;
};

type ReportDto = {
  data: Array<ConversationDto>;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  windowUsed?: WindowUsedDto;
};

type ExportJobStatus =
  | "PENDING"
  | "PROCESSING"
  | "RETRYING"
  | "COMPLETED"
  | "FAILED"
  | "EXPIRED";

type ExportFiltersAppliedDto = {
  search?: string;
  attendantId?: string;
  channel?: string;
  withAppointment?: boolean;
  start?: string;
  end?: string;
  tz?: string;
  granularity?: ReportGranularity;
  year?: number;
  month?: number;
};

type ExportJobDto = {
  id: string;
  status: ExportJobStatus;
  attempts: number;
  maxAttempts: number;
  errorMessage?: string;
  fileName?: string;
  fileSizeBytes?: number;
  contentType?: string;
  expiresAt?: string;
  downloadUrl?: string;
  windowUsed: WindowUsedDto;
  layoutVersion?: string;
  layoutColumns?: string[];
  filtersApplied?: ExportFiltersAppliedDto;
  createdAt: string;
  updatedAt: string;
};

type PeriodSelectionPreset = ReportPreset | "custom";

type SystemUserListDto = {
  data: Array<{
    id?: string;
    name: string;
  }>;
};

type ReportRequestOptions = {
  pageNumber?: number;
  searchValue?: string;
  start?: Date | null;
  end?: Date | null;
  appointmentFilter?: boolean;
  attendantIdValue?: string;
  channelValue?: string;
  limitValue?: number;
};

const PRESET_OPTIONS: Array<{ label: string; value: PeriodSelectionPreset }> = [
  { label: "Hoje", value: "today" },
  { label: "Últimos 7 dias", value: "last7Days" },
  { label: "Últimos 30 dias", value: "last30Days" },
  { label: "Mês atual", value: "currentMonth" },
  { label: "Personalizado", value: "custom" },
];

const STATUS_LABELS: Record<ClosedConversationStatus, string> = {
  CLOSED_BY_AI: "Encerrada por IA",
  CLOSED_BY_HUMAN_ATTENDANCE: "Encerrada por humano",
  CLOSED_BY_TIMEOUT: "Encerrada por timeout",
};

const CHANNEL_FILTER_OPTIONS = [
  { label: "Todos os canais", value: "all" },
  { label: "WhatsApp", value: "whatsapp" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function formatStatusLabel(status: ClosedConversationStatus): string {
  return STATUS_LABELS[status] ?? status;
}

function formatChannelLabel(channel: string): string {
  const normalized = channel?.trim().toLowerCase();
  if (!normalized) return "Não informado";
  if (normalized === "whatsapp") return "WhatsApp";
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
}

function formatExportJobStatus(status: ExportJobStatus): string {
  switch (status) {
    case "PENDING":
      return "Na fila";
    case "PROCESSING":
      return "Processando";
    case "RETRYING":
      return "Reprocessando";
    case "COMPLETED":
      return "Concluído";
    case "FAILED":
      return "Falhou";
    case "EXPIRED":
      return "Expirado";
    default:
      return status;
  }
}

function formatExportFilters(
  filtersApplied: ExportFiltersAppliedDto | undefined,
  attendants: Array<{ id: string; name: string }>,
): string {
  if (!filtersApplied) return "Sem filtros adicionais";

  const parts: string[] = [];

  if (filtersApplied.search) {
    parts.push(`Busca: ${filtersApplied.search}`);
  }

  if (filtersApplied.attendantId) {
    const attendantName =
      attendants.find((item) => item.id === filtersApplied.attendantId)?.name ??
      filtersApplied.attendantId;
    parts.push(`Atendente: ${attendantName}`);
  }

  if (filtersApplied.channel) {
    parts.push(`Canal: ${formatChannelLabel(filtersApplied.channel)}`);
  }

  if (filtersApplied.withAppointment) {
    parts.push("Somente com agendamento");
  }

  return parts.length > 0 ? parts.join(" | ") : "Sem filtros adicionais";
}

function formatExportWindow(windowUsed: WindowUsedDto): string {
  const start = new Date(windowUsed.start);
  const end = new Date(windowUsed.end);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${windowUsed.start} até ${windowUsed.end}`;
  }

  const startLabel = start.toLocaleString("pt-BR", {
    timeZone: windowUsed.tz,
  });
  const endLabel = end.toLocaleString("pt-BR", {
    timeZone: windowUsed.tz,
  });

  return `${startLabel} até ${endLabel}`;
}

function ReportTableSkeleton() {
  return (
    <div className="app-card animate-pulse overflow-hidden">
      <div className="grid grid-cols-7 gap-4 border-b border-slate-200 bg-slate-50 p-4">
        {Array.from({ length: 7 }).map((_, index) => (
          <div key={index} className="h-3 rounded bg-slate-200" />
        ))}
      </div>
      <div className="space-y-3 p-4">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="h-4 rounded bg-slate-100" />
        ))}
      </div>
    </div>
  );
}

export default function ConversationsReport() {
  const { error, info, success, warn } = useToast();
  const { user } = useAuth();

  const initialRange = resolvePresetRange("currentMonth");
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [attendants, setAttendants] = useState<
    Array<{ id: string; name: string }>
  >([]);

  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [attendantFilter, setAttendantFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");

  const [isExporting, setIsExporting] = useState(false);
  const [exportJobId, setExportJobId] = useState<string | null>(null);
  const [exportJob, setExportJob] = useState<ExportJobDto | null>(null);
  const [lastDownloadedJobId, setLastDownloadedJobId] = useState<string | null>(
    null,
  );
  const [openingConversationId, setOpeningConversationId] = useState<
    string | null
  >(null);

  const debouncedSearch = useDebounce(search, 500);

  const [preset, setPreset] = useState<PeriodSelectionPreset>("currentMonth");
  const [startDate, setStartDate] = useState<Date | null>(
    initialRange.startDate,
  );
  const [endDate, setEndDate] = useState<Date | null>(initialRange.endDate);
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const [granularity, setGranularity] = useState<ReportGranularity>(
    initialRange.granularity,
  );

  const [withAppointment, setWithAppointment] = useState(false);
  const [isSchedulingEnabled, setIsSchedulingEnabled] = useState<
    boolean | null
  >(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const timezoneOptions = useMemo(() => {
    const options = [timezone, "America/Sao_Paulo", "America/Fortaleza", "UTC"];
    return Array.from(new Set(options));
  }, [timezone]);

  const columns = [
    { key: "userName", label: "Nome" },
    {
      key: "dialCode",
      label: "Telefone",
      render: (conversation: ConversationDto) =>
        conversation.countryCode && conversation.dialCode
          ? formatPhoneWithCountryCode(
              conversation.dialCode,
              conversation.countryCode,
            )
          : "-",
    },
    {
      key: "status",
      label: "Status",
      render: (conversation: ConversationDto) =>
        formatStatusLabel(conversation.status),
    },
    {
      key: "systemUserName",
      label: "Atendente",
      render: (conversation: ConversationDto) =>
        conversation.systemUserName || "Sem atendente",
    },
    {
      key: "channel",
      label: "Canal",
      render: (conversation: ConversationDto) =>
        formatChannelLabel(conversation.channel),
    },
    { key: "createdAt", label: "Data de encerramento" },
    {
      key: "actions",
      label: "Ações",
      render: (conversation: ConversationDto) => (
        <LoadingButton
          onClick={() => openClosedConversation(conversation.id)}
          className="btn-link-primary cursor-pointer"
          isLoading={openingConversationId === conversation.id}
          loadingLabel="Carregando"
        >
          <Eye className="w-4 h-4" />
        </LoadingButton>
      ),
    },
  ];

  useEffect(() => {
    if (!user?.appName) return;

    const fetchTenant = async () => {
      try {
        const response = await api.get<Tenant>(`/tenant/${user.appName}`);
        setIsSchedulingEnabled(response.data.enabledScheduling);
      } catch (err) {
        error(getApiErrorMessage(err));
        setIsSchedulingEnabled(false);
      }
    };

    fetchTenant();
  }, [user?.appName, error]);

  useEffect(() => {
    if (isSchedulingEnabled === false) {
      setWithAppointment(false);
    }
  }, [isSchedulingEnabled]);

  useEffect(() => {
    const fetchAttendants = async () => {
      try {
        const response = await api.get<SystemUserListDto>("/system-user", {
          params: {
            page: 1,
            limit: 100,
          },
        });

        const nextAttendants = response.data.data
          .filter((item): item is { id: string; name: string } =>
            Boolean(item.id),
          )
          .sort((left, right) => left.name.localeCompare(right.name));

        setAttendants(nextAttendants);
      } catch (err) {
        error(getApiErrorMessage(err));
      }
    };

    fetchAttendants();
  }, [error]);

  const getReport = useCallback(
    async (options: ReportRequestOptions = {}) => {
      const {
        pageNumber = 1,
        searchValue = search,
        start = startDate,
        end = endDate,
        appointmentFilter = withAppointment,
        attendantIdValue = attendantFilter,
        channelValue = channelFilter,
        limitValue = limit,
      } = options;

      setIsLoadingReport(true);
      setReportError(null);

      try {
        const response = await api.get<ReportDto>("/report/conversations", {
          params: {
            page: pageNumber,
            limit: limitValue,
            search: searchValue,
            attendantId:
              attendantIdValue !== "all" ? attendantIdValue : undefined,
            channel: channelValue !== "all" ? channelValue : undefined,
            ...toReportPeriodQuery({
              startDate: start,
              endDate: end,
              timezone,
              granularity,
            }),
            withAppointment: appointmentFilter ? "true" : undefined,
          },
        });

        setConversations(response.data.data);
        setPage(response.data.page);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
      } catch (err) {
        const message = getApiErrorMessage(err);
        setReportError(message);
        error(message);
      } finally {
        setIsLoadingReport(false);
      }
    },
    [
      search,
      startDate,
      endDate,
      withAppointment,
      attendantFilter,
      channelFilter,
      limit,
      timezone,
      granularity,
      error,
    ],
  );

  useEffect(() => {
    const appointmentFilter =
      isSchedulingEnabled === true ? withAppointment : false;

    getReport({
      pageNumber: 1,
      searchValue: debouncedSearch,
      start: startDate,
      end: endDate,
      appointmentFilter,
      attendantIdValue: attendantFilter,
      channelValue: channelFilter,
      limitValue: limit,
    });
  }, [
    debouncedSearch,
    startDate,
    endDate,
    withAppointment,
    attendantFilter,
    channelFilter,
    limit,
    getReport,
    isSchedulingEnabled,
  ]);

  async function openClosedConversation(id: string) {
    if (openingConversationId) return;
    try {
      setOpeningConversationId(id);
      const response = await api.get(`/conversation/get-closed-by-id/${id}`);
      setSelectedConversation(response.data);
      setModalOpen(true);
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setOpeningConversationId(null);
    }
  }

  function triggerExportDownload(url: string, fileName?: string) {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "atendimentos-encerrados.csv";
    link.click();
  }

  const getExportJobStatus = useCallback(
    async (jobId: string) => {
      try {
        const response = await api.get<ExportJobDto>(
          `/report/conversations/export-jobs/${jobId}`,
        );

        const nextJob = response.data;
        setExportJob(nextJob);

        if (nextJob.status === "COMPLETED" && nextJob.downloadUrl) {
          if (lastDownloadedJobId !== nextJob.id) {
            triggerExportDownload(nextJob.downloadUrl, nextJob.fileName);
            setLastDownloadedJobId(nextJob.id);
            success("Exportação concluída. Download iniciado.");
          }

          setIsExporting(false);
          setExportJobId(null);
          return;
        }

        if (nextJob.status === "FAILED") {
          setIsExporting(false);
          setExportJobId(null);
          error(
            nextJob.errorMessage || "Falha ao gerar arquivo de exportação.",
          );
          return;
        }

        if (nextJob.status === "EXPIRED") {
          setIsExporting(false);
          setExportJobId(null);
          warn("O arquivo de exportação expirou. Gere um novo export.");
        }
      } catch (err) {
        setIsExporting(false);
        setExportJobId(null);
        error(getApiErrorMessage(err));
      }
    },
    [error, lastDownloadedJobId, success, warn],
  );

  useEffect(() => {
    if (!exportJobId) return;

    let active = true;
    let requestInFlight = false;

    const poll = async () => {
      if (!active || requestInFlight) return;
      requestInFlight = true;
      try {
        await getExportJobStatus(exportJobId);
      } finally {
        requestInFlight = false;
      }
    };

    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, 3000);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [exportJobId, getExportJobStatus]);

  async function exportCsv() {
    if (isExporting) return;

    try {
      setIsExporting(true);
      setLastDownloadedJobId(null);
      const appointmentFilter =
        isSchedulingEnabled === true ? withAppointment : false;

      const response = await api.post<ExportJobDto>(
        "/report/conversations/export-jobs",
        {
          search,
          attendantId: attendantFilter !== "all" ? attendantFilter : undefined,
          channel: channelFilter !== "all" ? channelFilter : undefined,
          ...toReportPeriodQuery({
            startDate,
            endDate,
            timezone,
            granularity,
          }),
          withAppointment: appointmentFilter || undefined,
        },
      );

      setExportJob(response.data);
      setExportJobId(response.data.id);
      info(
        "Exportação iniciada. O arquivo será baixado quando estiver pronto.",
      );
    } catch (err) {
      error(getApiErrorMessage(err));
      setIsExporting(false);
    }
  }

  function onPresetChange(value: PeriodSelectionPreset) {
    setPreset(value);
    if (value === "custom") return;

    const range = resolvePresetRange(value);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setGranularity(range.granularity);
  }

  function onStartDateChange(date: Date | null) {
    setPreset("custom");
    setGranularity("day");
    if (!date) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    setStartDate(date);
    if (endDate && endDate < date) {
      setEndDate(date);
    }
  }

  function onEndDateChange(date: Date | null) {
    setPreset("custom");
    setGranularity("day");
    if (!date) {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    setEndDate(date);
  }

  function retryReport() {
    getReport({
      pageNumber: page,
      searchValue: debouncedSearch,
      start: startDate,
      end: endDate,
      appointmentFilter: isSchedulingEnabled === true ? withAppointment : false,
      attendantIdValue: attendantFilter,
      channelValue: channelFilter,
      limitValue: limit,
    });
  }

  return (
    <div className="app-page-compact">
      <BackButton />
      <div className="mb-6 flex items-center justify-between">
        <h1 className="app-page-title">
          <span className="inline-flex items-center gap-2">
            Atendimentos Realizados
            <MetricHelp
              helpText="Lista de conversas encerradas no período selecionado."
            />
          </span>
        </h1>
      </div>

      <div className="mb-4 flex w-full flex-col gap-4 sm:flex-row sm:items-end">
        <div className="flex w-full flex-wrap gap-4">
          <input
            className="form-input w-full sm:w-56"
            type="text"
            placeholder="Nome ou telefone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="form-input w-full sm:w-52"
            value={preset}
            onChange={(e) =>
              onPresetChange(e.target.value as PeriodSelectionPreset)
            }
          >
            {PRESET_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <DatePicker
            selected={startDate}
            onChange={onStartDateChange}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            placeholderText="Data inicial"
            className="form-input w-full sm:w-48"
            dateFormat="dd/MM/yyyy"
            isClearable
          />

          <DatePicker
            selected={endDate}
            onChange={onEndDateChange}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate || undefined}
            placeholderText="Data final"
            className="form-input w-full sm:w-48"
            dateFormat="dd/MM/yyyy"
            isClearable
          />

          <select
            className="form-input w-full sm:w-56"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {timezoneOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          <select
            className="form-input w-full sm:w-56"
            value={attendantFilter}
            onChange={(e) => setAttendantFilter(e.target.value)}
          >
            <option value="all">Todos os atendentes</option>
            {attendants.map((attendant) => (
              <option key={attendant.id} value={attendant.id}>
                {attendant.name}
              </option>
            ))}
          </select>

          <select
            className="form-input w-full sm:w-44"
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)}
          >
            {CHANNEL_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="form-input w-full sm:w-36"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size} / página
              </option>
            ))}
          </select>

          {isSchedulingEnabled && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={withAppointment}
                onChange={(e) => setWithAppointment(e.target.checked)}
              />
              Atendimentos com agendamento associado
            </label>
          )}
        </div>

        <div className="ml-auto flex w-full flex-col items-end gap-2 sm:w-auto">
          <LoadingButton
            type="button"
            onClick={exportCsv}
            className="btn-link-success flex h-8 w-8 cursor-pointer items-center justify-center"
            isLoading={isExporting}
            loadingLabel="Preparando"
            loaderSize={20}
          >
            <FileSpreadsheet className="h-8 w-8" />
          </LoadingButton>
          <div className="w-20 text-right text-[12px] leading-4 text-slate-500">
            CSV gerencial
          </div>

          {exportJob && (
            <div className="max-w-64 text-right text-xs text-slate-600">
              <div>
                Exportação:{" "}
                <span className="font-semibold">
                  {formatExportJobStatus(exportJob.status)}
                </span>
              </div>
              <div>
                Tentativas: {exportJob.attempts}/{exportJob.maxAttempts}
              </div>
              <div>
                Layout:{" "}
                <span className="font-semibold">
                  Gerencial{" "}
                  {String(exportJob.layoutVersion ?? "v2").toUpperCase()}
                </span>
              </div>
              <div>
                Colunas:{" "}
                <span className="font-semibold">
                  {exportJob.layoutColumns?.length ?? 0}
                </span>
              </div>
              <div className="mt-1">
                Janela: {formatExportWindow(exportJob.windowUsed)}
              </div>
              <div className="mt-1">
                Filtros:{" "}
                {formatExportFilters(exportJob.filtersApplied, attendants)}
              </div>
              {exportJob.status === "FAILED" && exportJob.errorMessage && (
                <div className="mt-1 text-red-600">
                  {exportJob.errorMessage}
                </div>
              )}
              {exportJob.status === "COMPLETED" && exportJob.downloadUrl && (
                <button
                  type="button"
                  className="btn-link-primary mt-1"
                  onClick={() =>
                    triggerExportDownload(
                      exportJob.downloadUrl,
                      exportJob.fileName,
                    )
                  }
                >
                  Baixar novamente
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {reportError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div>{reportError}</div>
          <button
            type="button"
            className="btn-link-danger mt-2"
            onClick={retryReport}
          >
            Tentar novamente
          </button>
        </div>
      )}

      {isLoadingReport ? (
        <ReportTableSkeleton />
      ) : (
        <Table<ConversationDto>
          columns={columns}
          data={conversations}
          page={page}
          total={total}
          totalPages={totalPages}
          emptyMessage={
            reportError
              ? "Não foi possível carregar o relatório com os filtros atuais."
              : undefined
          }
          onPageChange={(newPage) =>
            getReport({
              pageNumber: newPage,
              searchValue: debouncedSearch,
              start: startDate,
              end: endDate,
              appointmentFilter:
                isSchedulingEnabled === true ? withAppointment : false,
              attendantIdValue: attendantFilter,
              channelValue: channelFilter,
              limitValue: limit,
            })
          }
        />
      )}

      <ViewConversationModal
        isOpen={modalOpen}
        conversation={selectedConversation}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
